import { App, Skeleton } from 'antd';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  broadcastApi,
  broadcastMediaUrl,
  broadcastQueryKeys,
  broadcastV2Api,
  useBroadcast,
  useBroadcastStats,
  type Broadcast,
  type BroadcastButton,
} from '@entities/broadcast';
import { botLink, useChannelCards } from '@entities/channel';
import { extractErrorMessage } from '@shared/api';
import { num, plural, useUnsavedGuard } from '@shared/ui/v2';
import { ChBadge } from '../channels/chUi';
import { BcStatus } from './bcUi';
import './broadcasts.css';

const MAX_MEDIA = 10;
const MAX_BUTTONS = 3;
const TEST_KEY = (bid: string) => `richat.bcTest.${bid}`;
/** Кому слали тест с этого бота — чтобы в следующий раз не просить код. */
const ME_KEY = (channelId: string) => `richat.bcTestMe.${channelId}`;
const POLL_MS = 1000;

function ls(key: string, val?: string) {
  try {
    if (val === undefined) return localStorage.getItem(key);
    localStorage.setItem(key, val);
  } catch {
    /* нет хранилища — просто не запомним */
  }
  return null;
}

const timeMsk = (iso: string) =>
  new Date(iso).toLocaleTimeString('ru-RU', {
    timeZone: 'Europe/Moscow',
    hour: '2-digit',
    minute: '2-digit',
  });

/** Экран 9а «Рассылка» (утверждён 28.10.2026). Черновик сохраняется сам. */
export function BroadcastEditPage() {
  const { id = '', bid = '' } = useParams<{ id: string; bid: string }>();
  const { message, modal } = App.useApp();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { data: b, error, isLoading } = useBroadcast(id, bid);
  const { data: channels } = useChannelCards(id);
  const bots = (channels ?? []).filter((c) => c.platform !== 'widget');
  const draft = b?.status === 'draft';
  const live = b?.status === 'queued' || b?.status === 'sending';
  const { data: stats } = useBroadcastStats(id, b && !draft ? bid : null, !!live);

  // Поля черновика — локально, сохраняем сами через 0,8 с.
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [buttons, setButtons] = useState<BroadcastButton[]>([]);
  const [saving, setSaving] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const loaded = useRef<string | null>(null);
  useEffect(() => {
    if (b && loaded.current !== b.id) {
      loaded.current = b.id;
      setTitle(b.title ?? '');
      setBody(b.body_text);
      setButtons(b.buttons);
    }
  }, [b]);

  const validButtons = buttons.filter(
    (x) => x.text.trim() && /^https?:\/\/\S+\.\S+/.test(x.url.trim()),
  );
  const sig = JSON.stringify([title, body, validButtons]);
  const savedSig = useRef<string>('');
  useEffect(() => {
    if (b && loaded.current === b.id && !savedSig.current)
      savedSig.current = JSON.stringify([b.title ?? '', b.body_text, b.buttons]);
  }, [b]);
  useEffect(() => {
    if (!b || !draft || !savedSig.current || sig === savedSig.current) return undefined;
    const t = setTimeout(async () => {
      setSaving('saving');
      try {
        const next = await broadcastApi.update(id, bid, {
          title: title.trim() || null,
          body_text: body,
          buttons: validButtons.map((x) => ({ text: x.text.trim(), url: x.url.trim() })),
        });
        savedSig.current = sig;
        qc.setQueryData(broadcastQueryKeys.detail(id, bid), next);
        qc.invalidateQueries({ queryKey: broadcastQueryKeys.list(id) });
        setSaving('saved');
      } catch (e) {
        setSaving('error');
        message.error(extractErrorMessage(e));
      }
    }, 800);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sig, draft]);

  // Уход со страницы раньше, чем сработал таймер: отправляем то, что не успело сохраниться.
  const pending = useRef<null | (() => void)>(null);
  pending.current =
    b && draft && savedSig.current && sig !== savedSig.current
      ? () => {
          broadcastApi
            .update(id, bid, {
              title: title.trim() || null,
              body_text: body,
              buttons: validButtons.map((x) => ({ text: x.text.trim(), url: x.url.trim() })),
            })
            .then(() => qc.invalidateQueries({ queryKey: broadcastQueryKeys.all }))
            .catch(() => undefined);
        }
      : null;
  useEffect(() => () => pending.current?.(), []);
  useUnsavedGuard(saving === 'error', 'Последние изменения рассылки не сохранились.');

  const flushSave = async () => {
    if (!b || !draft || sig === savedSig.current) return;
    const next = await broadcastApi.update(id, bid, {
      title: title.trim() || null,
      body_text: body,
      buttons: validButtons.map((x) => ({ text: x.text.trim(), url: x.url.trim() })),
    });
    savedSig.current = sig;
    qc.setQueryData(broadcastQueryKeys.detail(id, bid), next);
  };

  // «Проверьте на себе»: код → владелец пишет его боту → ловим и шлём тест.
  const [tested, setTested] = useState<{ to: string; at: string } | null>(() => {
    try {
      return JSON.parse(ls(TEST_KEY(bid)) || 'null');
    } catch {
      return null;
    }
  });
  const [code, setCode] = useState<{ value: string; until: number } | null>(null);
  const [me, setMe] = useState<{ id: string; name: string } | null>(null);
  const [wantCode, setWantCode] = useState(false);
  const [testErr, setTestErr] = useState<string | null>(null);
  const [testing, setTesting] = useState(false);
  const testingRef = useRef(false);
  const issuingRef = useRef(false);
  const [removing, setRemoving] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  const caughtRef = useRef<
    ((externalId: string, name: string, sent: boolean, error: string | null) => void) | null
  >(null);
  const [catching, setCatching] = useState(false);
  const getCodeRef = useRef<(() => Promise<void>) | null>(null);
  const channelId = b?.channel_id;
  // Кто получал тест с этого бота раньше — его помним, код не нужен.
  useEffect(() => {
    if (!channelId) return;
    setCode(null);
    setWantCode(false);
    try {
      setMe(JSON.parse(ls(ME_KEY(channelId)) || 'null'));
    } catch {
      setMe(null);
    }
  }, [channelId]);
  // Код показываем сразу, без лишней кнопки.
  useEffect(() => {
    if (draft && channelId && !code && (!me || wantCode)) getCodeRef.current?.();
  }, [draft, channelId, code, me, wantCode]);
  // Ждём, пока владелец напишет код боту; поймали — сразу шлём ему тест.
  useEffect(() => {
    if (!code) return;
    let alive = true;
    const t = window.setInterval(async () => {
      if (Date.now() > code.until) {
        setCode(null); // эффект выше сразу выдаст новый
        return;
      }
      try {
        const st = await broadcastV2Api.testCodeStatus(id, bid, code.value);
        // Тест сервер шлёт сам, как только бот получил код, — здесь только итог.
        if (alive && st.found && st.external_id && (st.sent || st.error)) {
          setCode(null);
          caughtRef.current?.(st.external_id, st.name || st.external_id, st.sent, st.error);
        } else if (alive && st.found) {
          setCatching(true);
        }
      } catch {
        /* сеть моргнула — спросим ещё раз через 2 с */
      }
    }, POLL_MS);
    return () => {
      alive = false;
      window.clearInterval(t);
    };
  }, [code, id, bid]);

  if (isLoading) return <Skeleton active style={{ padding: 24 }} />;
  if (error || !b)
    return (
      <div className="v2-content">
        <div className="v2-card v2-card-pad">
          {error
            ? `Не удалось загрузить рассылку: ${extractErrorMessage(error)}`
            : 'Рассылка не найдена.'}{' '}
          <Link to={`/assistants/${id}/broadcasts`}>Все рассылки</Link>
        </div>
      </div>
    );

  const bot = bots.find((c) => c.id === b.channel_id);
  const subs = bot?.subscribers ?? 0;
  const where = bot?.platform === 'max' ? 'MAX' : 'Telegram';
  const cache = (next: Broadcast) => {
    qc.setQueryData(broadcastQueryKeys.detail(id, bid), next);
    qc.invalidateQueries({ queryKey: broadcastQueryKeys.list(id) });
  };

  const pickBot = async (cid: string) => {
    if (!draft || cid === b.channel_id) return;
    try {
      await broadcastV2Api.changeChannel(id, bid, cid);
      qc.invalidateQueries({ queryKey: broadcastQueryKeys.detail(id, bid) });
      setTested(null);
    } catch (e) {
      message.error(extractErrorMessage(e));
    }
  };

  const upload = async (files: File[]) => {
    for (const f of files.slice(0, MAX_MEDIA - b.media.length)) {
      try {
        cache(await broadcastApi.uploadMedia(id, bid, f));
      } catch (e) {
        message.error(`${f.name}: ${extractErrorMessage(e)}`);
      }
    }
  };

  const removeMedia = async (i: number) => {
    if (removing !== null) return; // второй клик сдвинул бы список и удалил соседнее
    setRemoving(i);
    try {
      cache(await broadcastApi.deleteMedia(id, bid, i));
    } catch (e) {
      message.error(extractErrorMessage(e));
    } finally {
      setRemoving(null);
    }
  };

  /** Отправить тест конкретному человеку (его id в мессенджере). */
  const sendTestTo = async (externalId: string, name: string) => {
    if (testingRef.current || !b) return;
    const who = { id: externalId, name };
    setMe(who);
    setWantCode(false);
    ls(ME_KEY(b.channel_id), JSON.stringify(who));
    if (empty) {
      setTestErr('Нашли вас. Напишите текст сообщения и нажмите «Отправить мне».');
      return;
    }
    testingRef.current = true;
    setTesting(true);
    setTestErr(null);
    try {
      await flushSave();
      const r = await broadcastV2Api.sendTest(id, bid, externalId);
      const t = { to: r.sent_to || name, at: r.sent_at };
      setTested(t);
      ls(TEST_KEY(bid), JSON.stringify(t));
      const named = { id: externalId, name: r.sent_to || name };
      setMe(named);
      ls(ME_KEY(b.channel_id), JSON.stringify(named));
    } catch (e) {
      setTestErr(extractErrorMessage(e));
    } finally {
      testingRef.current = false;
      setTesting(false);
    }
  };

  caughtRef.current = (externalId, name, sent, error) => {
    setCatching(false);
    const who = { id: externalId, name };
    setMe(who);
    setWantCode(false);
    if (b) ls(ME_KEY(b.channel_id), JSON.stringify(who));
    if (sent) {
      const t = { to: name, at: new Date().toISOString() };
      setTested(t);
      setTestErr(null);
      ls(TEST_KEY(bid), JSON.stringify(t));
    } else {
      setTestErr(error);
    }
  };

  const getCode = async () => {
    if (issuingRef.current) return;
    issuingRef.current = true;
    setTestErr(null);
    try {
      const r = await broadcastV2Api.testCode(id, bid);
      setCode({ value: r.code, until: Date.now() + r.ttl_sec * 1000 });
    } catch (e) {
      setTestErr(extractErrorMessage(e));
    } finally {
      issuingRef.current = false;
    }
  };
  getCodeRef.current = getCode;

  const sendAll = () =>
    modal.confirm({
      title: `Отправить всем · ${num(subs)}?`,
      content: 'Сообщение уйдёт всем подписчикам бота. Отправленное сообщение нельзя отозвать.',
      okText: 'Отправить',
      cancelText: 'Отмена',
      onOk: async () => {
        setBusy(true);
        try {
          await flushSave();
          cache(await broadcastApi.send(id, bid));
        } catch (e) {
          message.error(extractErrorMessage(e));
        } finally {
          setBusy(false);
        }
      },
    });

  const stop = () =>
    modal.confirm({
      title: 'Остановить рассылку?',
      content: 'Тем, кому уже отправили, сообщение останется. Остальным не уйдёт.',
      okText: 'Остановить',
      okButtonProps: { danger: true },
      cancelText: 'Продолжить',
      onOk: async () => {
        try {
          cache(await broadcastApi.cancel(id, bid));
        } catch (e) {
          message.error(extractErrorMessage(e));
        }
      },
    });

  const removeDraft = () =>
    modal.confirm({
      title: 'Удалить черновик?',
      okText: 'Удалить',
      okButtonProps: { danger: true },
      cancelText: 'Отмена',
      onOk: async () => {
        try {
          await broadcastApi.remove(id, bid);
          qc.invalidateQueries({ queryKey: broadcastQueryKeys.list(id) });
          navigate(`/assistants/${id}/broadcasts`, { replace: true });
        } catch (e) {
          message.error(extractErrorMessage(e));
        }
      },
    });

  const empty = !body.trim() && !b.media.length;
  // Счётчики по получателям (stats) точнее; пока снимка нет — счётчики рассылки.
  const st = stats && stats.total > 0 ? stats : null;
  const total = st?.total ?? b.total_count;
  const sent = st?.sent ?? b.sent_count;
  const failed = st ? st.failed + st.blocked : b.failed_count;
  const left = st?.pending ?? Math.max(0, total - sent - failed);
  const pct = total ? Math.round((100 * (sent + failed)) / total) : 0;

  return (
    <>
      <header className="v2-topbar is-section">
        <Link to={`/assistants/${id}/broadcasts`} className="mp-back">
          <svg
            width="15"
            height="15"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="M15 18l-6-6 6-6" />
          </svg>
          Рассылки
        </Link>
        <span style={{ color: '#D1D5DB' }}>/</span>
        <h1>{title.trim() || 'Без названия'}</h1>
        <BcStatus status={b.status} />
        {draft && (
          <span className="v2-xs v2-muted bc-saved" aria-live="polite">
            {saving === 'saving'
              ? 'Сохраняем…'
              : saving === 'saved'
                ? 'Сохранено'
                : saving === 'error'
                  ? 'Не сохранилось'
                  : ''}
          </span>
        )}
      </header>
      <div className="v2-content is-section">
        <div className="bc-grid">
          <div className="bc-main">
            <section className="v2-card bc-card" aria-labelledby="h-to">
              <h2 id="h-to" className="v2-h2">
                Кому
              </h2>
              <label className="bc-label" htmlFor="bc-title">
                Название — видно только вам
              </label>
              <input
                id="bc-title"
                className="st-input"
                maxLength={255}
                value={title}
                disabled={!draft}
                onChange={(e) => setTitle(e.target.value)}
              />
              <div className="bc-bots" role="radiogroup" aria-label="Бот">
                {bots.map((c) => (
                  <button
                    key={c.id}
                    type="button"
                    role="radio"
                    aria-checked={c.id === b.channel_id}
                    disabled={!draft && c.id !== b.channel_id}
                    className={c.id === b.channel_id ? 'bc-bot is-on' : 'bc-bot'}
                    onClick={() => pickBot(c.id)}
                  >
                    <ChBadge kind={c.platform as 'telegram' | 'max'} />
                    <span style={{ minWidth: 0 }}>
                      <span className="bc-bot-name">
                        {c.platform === 'max' ? c.bot_display_name || c.title : c.title}
                      </span>
                      <span className="v2-xs v2-muted">
                        {c.platform === 'max' ? 'MAX' : 'Telegram'} · {num(c.subscribers)}{' '}
                        {plural(c.subscribers, 'подписчик', 'подписчика', 'подписчиков')}
                      </span>
                    </span>
                  </button>
                ))}
              </div>
              <div className="v2-xs v2-muted">Канал нельзя поменять после первой отправки.</div>
            </section>

            <section className="v2-card bc-card" aria-labelledby="h-msg">
              <h2 id="h-msg" className="v2-h2">
                Сообщение
              </h2>
              <label htmlFor="bc-body" className="v2-sr">
                Текст сообщения
              </label>
              <textarea
                id="bc-body"
                className="st-input bc-text"
                rows={6}
                maxLength={4000}
                value={body}
                disabled={!draft}
                placeholder="Например: только до 10 ноября — чистка кофемашины со скидкой 20%…"
                onChange={(e) => setBody(e.target.value)}
              />
              <div className="bc-label">
                Вложения{' '}
                <span className="v2-muted">— картинки, видео, документы, до {MAX_MEDIA}</span>
              </div>
              <div className="bc-media">
                {b.media.map((m, i) => (
                  <div key={m.s3_key} className="bc-thumb" title={m.filename ?? undefined}>
                    {m.type === 'image' ? (
                      <img src={broadcastMediaUrl(b.id, m.s3_key)} alt={m.filename ?? 'картинка'} />
                    ) : (
                      <span className="bc-thumb-doc">
                        {m.type === 'video'
                          ? 'видео'
                          : (m.filename?.split('.').pop() ?? 'файл').toUpperCase()}
                      </span>
                    )}
                    {draft && (
                      <button
                        type="button"
                        aria-label="Убрать вложение"
                        disabled={removing !== null}
                        onClick={() => removeMedia(i)}
                      >
                        ✕
                      </button>
                    )}
                  </div>
                ))}
                {!draft && !b.media.length && <span className="v2-small v2-muted">нет</span>}
                {draft && b.media.length < MAX_MEDIA && (
                  <button
                    type="button"
                    className="bc-thumb bc-add"
                    aria-label="Добавить вложение"
                    onClick={() => fileRef.current?.click()}
                  >
                    +
                  </button>
                )}
                <input
                  ref={fileRef}
                  type="file"
                  multiple
                  hidden
                  onChange={(e) => {
                    const fs = Array.from(e.target.files ?? []);
                    e.target.value = '';
                    if (fs.length) upload(fs);
                  }}
                />
              </div>
              <div className="bc-label">
                Кнопки под сообщением{' '}
                <span className="v2-muted">— до {MAX_BUTTONS}, со ссылками</span>
              </div>
              {buttons.map((x, i) => {
                const badUrl = !!x.url.trim() && !/^https?:\/\/\S+\.\S+/.test(x.url.trim());
                return (
                  // eslint-disable-next-line react/no-array-index-key
                  <div key={i} className="bc-btn-row">
                    <input
                      className="st-input"
                      maxLength={64}
                      placeholder="Текст кнопки"
                      aria-label={`Текст кнопки ${i + 1}`}
                      value={x.text}
                      disabled={!draft}
                      onChange={(e) =>
                        setButtons((bs) =>
                          bs.map((y, j) => (j === i ? { ...y, text: e.target.value } : y)),
                        )
                      }
                    />
                    <input
                      className={badUrl ? 'st-input is-err' : 'st-input'}
                      placeholder="https://…"
                      aria-label={`Ссылка кнопки ${i + 1}`}
                      aria-invalid={badUrl}
                      value={x.url}
                      disabled={!draft}
                      onChange={(e) =>
                        setButtons((bs) =>
                          bs.map((y, j) => (j === i ? { ...y, url: e.target.value } : y)),
                        )
                      }
                    />
                    {draft && (
                      <button
                        type="button"
                        className="bc-x"
                        aria-label="Убрать кнопку"
                        onClick={() => setButtons((bs) => bs.filter((_, j) => j !== i))}
                      >
                        ✕
                      </button>
                    )}
                  </div>
                );
              })}
              {!draft && !buttons.length && <span className="v2-small v2-muted">нет</span>}
              {draft && buttons.length < MAX_BUTTONS && (
                <button
                  type="button"
                  className="bc-add-btn"
                  onClick={() => setButtons((bs) => [...bs, { text: '', url: '' }])}
                >
                  + Добавить кнопку
                </button>
              )}
            </section>

            {draft && (
              <div className="bc-foot">
                <span className="v2-xs v2-muted">
                  Черновик сохраняется сам. Отправленное сообщение нельзя отозвать.
                </span>
                <button type="button" className="v2-btn bc-danger" onClick={removeDraft}>
                  Удалить черновик
                </button>
              </div>
            )}
          </div>

          <aside className="bc-side">
            {draft ? (
              <section className="v2-card bc-card" aria-labelledby="h-test">
                <h2 id="h-test" className="v2-h2">
                  Проверьте на себе
                </h2>
                {me && !wantCode ? (
                  <>
                    <p className="v2-small v2-muted" style={{ margin: 0 }}>
                      Пришлём тест вам в {where} — как в прошлый раз.
                    </p>
                    <div className="bc-test-row">
                      <button
                        type="button"
                        className="v2-btn"
                        disabled={testing || empty}
                        onClick={() => sendTestTo(me.id, me.name)}
                      >
                        {testing ? 'Отправляем…' : `Отправить мне · ${me.name}`}
                      </button>
                    </div>
                    <button
                      type="button"
                      className="v2-link bc-link-btn"
                      onClick={() => {
                        setCode(null);
                        setWantCode(true);
                      }}
                    >
                      Другой аккаунт — получить код
                    </button>
                  </>
                ) : (
                  <ol className="bc-steps bc-code-steps" aria-live="polite">
                    <li>
                      Откройте бота{' '}
                      {bot && botLink(bot) ? (
                        <a
                          href={botLink(bot) ?? undefined}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="v2-link"
                        >
                          {bot.title} ↗
                        </a>
                      ) : (
                        <b>{bot?.title ?? ''}</b>
                      )}{' '}
                      в {where} со своего аккаунта.
                    </li>
                    <li>
                      Отправьте ему код:
                      <div className="bc-code-value">{code?.value ?? '····'}</div>
                    </li>
                    <li>
                      {catching
                        ? 'Код получен — отправляем тест…'
                        : 'Бот узнает вас и за пару секунд пришлёт тестовую рассылку. Код действует 15 минут.'}
                    </li>
                  </ol>
                )}
                {testErr && (
                  <div role="alert" className="ch-err">
                    {testErr}
                  </div>
                )}
                {tested && !testErr && (
                  <div className="bc-ok" role="status">
                    ✓ Отправили вам ({tested.to}) в {timeMsk(tested.at)}
                  </div>
                )}
                <button
                  type="button"
                  className="v2-btn is-primary bc-send"
                  disabled={busy || empty || !subs}
                  onClick={sendAll}
                >
                  <svg
                    width="15"
                    height="15"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.8"
                    aria-hidden="true"
                  >
                    <path d="M22 2L11 13M22 2l-7 20-4-9-9-4z" />
                  </svg>
                  Отправить всем · {num(subs)}
                </button>
                <div className="v2-xs v2-muted">
                  {empty
                    ? 'Напишите текст или добавьте вложение.'
                    : !subs
                      ? 'У бота пока нет подписчиков — ими станут те, кто ему напишет.'
                      : tested
                        ? 'Проверка пройдена — можно отправлять всем.'
                        : 'Советуем сначала отправить себе и посмотреть, как выглядит.'}
                </div>
              </section>
            ) : (
              <section className="v2-card bc-card" aria-labelledby="h-del">
                <h2 id="h-del" className="v2-h2">
                  Доставка
                </h2>
                <div className="bc-bar is-big">
                  <i style={{ width: `${pct}%` }} />
                </div>
                <div className="bc-kv">
                  <span>Доставлено</span>
                  <b>{num(sent)}</b>
                </div>
                <div className="bc-kv">
                  <span>Не дошло (заблокировали бота)</span>
                  <b className={failed ? 'bc-fail' : undefined}>{num(failed)}</b>
                </div>
                {(live || b.status === 'cancelled') && (
                  <div className="bc-kv">
                    <span>
                      {b.status === 'cancelled' ? 'Не отправлено (остановлена)' : 'Осталось'}
                    </span>
                    <b>{num(left)}</b>
                  </div>
                )}
                {live && (
                  <button type="button" className="v2-btn bc-danger" onClick={stop}>
                    Остановить
                  </button>
                )}
                {b.status === 'failed' && (
                  <div className="ch-err">
                    Рассылка не отправилась — проверьте, что бот подключён и отвечает.
                  </div>
                )}
              </section>
            )}
            <div className="v2-xs v2-muted" style={{ padding: '0 4px' }}>
              Ответы подписчиков попадут в «Диалоги» — на них ответит ассистент.
            </div>
          </aside>
        </div>
      </div>
    </>
  );
}
